import classNames from 'classnames'
import _ from 'lodash'
import React, {useContext} from 'react'
import {NullAsDefault, renderIf, renderMail, renderPhone,} from 'views/Shared'
import {IdentifierTypes} from "../../../AppLocals/appFieldTypes";
import {Avatar, message, Tag} from "antd";
import {FaAddressCard, FaUserGraduate} from "react-icons/fa6";
import {renderIfElse} from "../../../Shared";
import getTitle from "../../../../helpers/getTitle";
import copyToClipboard from "copy-to-clipboard";
import getLabelOfGender from "../../../../helpers/getLabelOfGender";
import {IoCopyOutline} from "react-icons/io5";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {StudentActionTypes} from "../../../AppLocals/Course";
import getAliasName from "../../../../helpers/getAliasName";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {GoDotFill} from "react-icons/go";
import {formatDate} from "../../../../helpers/datetimeHelpers";
import ImageLoader from "../../../../components/ImageLoader";
import {BsPeopleFill} from "react-icons/bs";
import {HiPhone} from "react-icons/hi2";
import {PiArrowsClockwiseBold, PiLinkSimpleBold} from "react-icons/pi";
import {MdMerge} from "react-icons/md";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";
import {studentLink_rotateSecretCode_Api} from "../../../../apis";

const STUDENT_STATUS_ARCHIVED = 9

const LinkSecretCodeCell = ({studentId, initialCode, t}) => {
    const [code, setCode] = React.useState(initialCode)
    const {isLoading, handleAsyncAction: rotateCode} = useAsyncAction({
        apiInfo: studentLink_rotateSecretCode_Api,
        onSuccess: (_, meta) => {
            const newCode = meta?.response?.data?.link_secret_code
            if (newCode) {
                setCode(newCode)
                message.success(t('link code rotated'))
            }
        },
        onError: () => message.error(t('failed to rotate code')),
    })

    if (!code) return null

    return (
        <div className="flex items-center gap-2 mt-1">
            <PiLinkSimpleBold size={11} className="text-indigo-400 flex-shrink-0"/>
            <span className="text-xs text-color-400">{`${t('link code')} :`}</span>
            <span
                onClick={() => {
                    copyToClipboard(code);
                    message.info(t('copied'))
                }}
                className="font-mono font-bold text-indigo-600 text-xs tracking-widest cursor-pointer hover:text-indigo-800 flex items-center gap-1">
                {code}
                <IoCopyOutline size={10}/>
            </span>
            <button
                disabled={isLoading}
                onClick={() => rotateCode({}, {id: studentId})}
                className="ml-1 flex items-center gap-1 text-xs text-color-400 hover:text-indigo-600 transition-colors disabled:opacity-40">
                <PiArrowsClockwiseBold size={12} className={isLoading ? 'animate-spin' : ''}/>
            </button>
        </div>
    )
}

export const renderStudent = ({translate: t, renderContextMenu = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    const {
        avatar,
        edit,
        user,
        fullname,
        gender,
        date_of_birth,
        place_of_birth,
        contact_phone,
        contact_email,
        student_idcode,
        link_secret_code,
        identifier_code,
        identifier_type,
        medal_awards = [],
        religious_order,
        religious_order_name,
        patron_saint_name,
        father,
        mother,
        status,
        merged_into_id,
    } = item ?? {}

    const isArchived = status === STUDENT_STATUS_ARCHIVED

    const {isSm} = useContext(LayoutContext)

    const hasFather = father?.name || father?.user?.name
    const hasMother = mother?.name || mother?.user?.name
    const fatherName = father?.user?.name || father?.name
    const fatherPhone = father?.phone
    const motherName = mother?.user?.name || mother?.name
    const motherPhone = mother?.phone

    return (
        <div key={index} className={classNames(
            "px-4 py-3 flex gap-3 custom-item-render relative list-item hover:background-100 transition-colors",
            isArchived && "opacity-60 bg-gray-50"
        )}>
            {/* Avatar */}
            {renderIfElse(
                avatar,
                <Avatar
                    size={38}
                    className="flex-shrink-0 mt-0.5 flex flex-center background-200"
                    src={avatar}
                    icon={<FaUserGraduate size={18} className="text-color-500"/>}
                />,
                <Avatar
                    size={38}
                    src={user?.avatar}
                    icon={<FaUserGraduate size={18} className="text-color-500"/>}
                    className="flex-shrink-0 mt-0.5 flex flex-center background-200"
                />
            )}

            {/* Main content */}
            <div className="flex flex-1 flex-col md:flex-row gap-2 md:gap-4 min-w-0 pr-8">

                {/* Student core info */}
                <div className="flex flex-col flex-1 min-w-0">
                    {/* Name */}
                    <h2
                        onClick={() => handleAction(StudentActionTypes.STUDENT_DETAIL, item)}
                        className="flex items-baseline gap-1.5 font-bold text-base text-color-000 hover:text-primary cursor-pointer leading-snug">
                        {item?.patron_saint && (
                            <span className="font-medium text-sm text-gray-600">
                                {getAliasName(item, 'patron_saint')}
                            </span>
                        )}
                        <span className="truncate">{fullname}</span>
                    </h2>

                    {isArchived && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <Tag
                                icon={<MdMerge size={11} className="inline mr-0.5"/>}
                                color="default"
                                className="text-xs font-medium text-gray-500 border-gray-300">
                                {t('archived')}
                            </Tag>
                            {merged_into_id && (
                                <span className="text-xs text-color-400 italic">{t('merged into another record')}</span>
                            )}
                        </div>
                    )}

                    {/* Codes row */}
                    <div className="flex items-center gap-3 flex-wrap mt-0.5">
                        {student_idcode && (
                            <div className="flex items-center gap-1 text-xs tracking-wide">
                                <span className="text-color-400">{`${t('student code')} :`}</span>
                                <span
                                    onClick={() => {
                                        copyToClipboard(student_idcode)
                                        message.info(t('copied'))
                                    }}
                                    className="font-mono font-semibold text-blue-500 hover:text-blue-700 cursor-pointer flex items-center gap-1">
                                    {student_idcode}
                                    <IoCopyOutline size={10}/>
                                </span>
                            </div>
                        )}
                        {edit === true && link_secret_code !== undefined && (
                            <LinkSecretCodeCell
                                studentId={item?.id}
                                initialCode={link_secret_code}
                                t={t}
                            />
                        )}
                        {(edit === true && identifier_code) && (
                            <div className="flex items-center gap-1 text-xs tracking-wide">
                                {isSm
                                    ? <FaAddressCard size={11} className="text-color-400"/>
                                    : <span
                                        className="text-color-400">{`${t(_.find(IdentifierTypes, {value: identifier_type})?.label)} :`}</span>
                                }
                                <span
                                    onClick={() => {
                                        copyToClipboard(identifier_code)
                                        message.info(t('copied'))
                                    }}
                                    className="font-mono font-semibold text-blue-500 hover:text-blue-700 cursor-pointer flex items-center gap-1">
                                    {identifier_code}
                                    <IoCopyOutline size={10}/>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Details compact row */}
                    <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-1 text-xs text-color-400 italic">
                        {date_of_birth && (
                            <span className="flex items-center gap-1">
                                <GoDotFill size={10} className="text-color-500 flex-shrink-0"/>
                                <span>{t('date of birth')} :</span>
                                <span className="text-color-200 font-medium not-italic">
                                    {formatDate(date_of_birth, 'DD/MM/YYYY')}
                                </span>
                            </span>
                        )}
                        {gender !== 0 && (
                            <span className="flex items-center gap-1">
                                <GoDotFill size={10} className="text-color-500 flex-shrink-0"/>
                                <span
                                    className="text-color-200 font-medium not-italic">{t(getLabelOfGender(gender))}</span>
                            </span>
                        )}
                        {contact_phone && (
                            <span className="flex items-center gap-1 text-color-200">
                                <GoDotFill size={10} className="text-color-500 flex-shrink-0"/>
                                {renderPhone(contact_phone)}
                            </span>
                        )}
                        {contact_email && (
                            <span className="flex items-center gap-1 text-color-200">
                                <GoDotFill size={10} className="text-color-500 flex-shrink-0"/>
                                {renderMail(contact_email)}
                            </span>
                        )}
                    </div>

                    {/* Religious order */}
                    {religious_order_name && (
                        <div className="mt-1 text-xs text-color-400 italic flex items-center gap-1">
                            <GoDotFill size={10} className="text-color-500 flex-shrink-0"/>
                            <span>{t('belong to religious order')} :</span>
                            <Link to={getLinkToDetail(religious_order)}
                                  className="no-underline text-blue-500 hover:text-blue-700">
                                {religious_order_name}
                            </Link>
                        </div>
                    )}

                    {/* Medals */}
                    {medal_awards?.length > 0 && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <GoDotFill size={10} className="text-color-500 flex-shrink-0"/>
                            <span className="text-xs text-color-500 italic">{`${t('certification awarded')} :`}</span>
                            {medal_awards.map((e, i) => (
                                <div key={i} className="flex items-center gap-1">
                                    <span className="font-bold text-xs text-primary italic">{getTitle(e.medal)}</span>
                                    {renderIf(
                                        e?.medal?.avatar,
                                        <ImageLoader src={e?.medal?.avatar} style={{width: 24, height: 20}}/>
                                    )}
                                    {i < medal_awards.length - 1 && <span className="text-color-300">,</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Parent info - right column on desktop */}
                {!isSm && (hasFather || hasMother) && (
                    <div className="flex flex-col gap-1 md:border-l md:border-color-50 md:pl-3 md:min-w-44 text-xs">
                        <div className="flex items-center gap-1 text-color-400 mb-0.5">
                            <BsPeopleFill size={11} className="text-color-300"/>
                            <span className="font-medium text-color-300 uppercase tracking-wide"
                                  style={{fontSize: 10}}>{t('parent information')}</span>
                        </div>
                        {fatherName && (
                            <div className="flex items-start gap-1 italic">
                                <span className="text-color-400 whitespace-no-wrap flex-shrink-0">{t('father')} :</span>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-color-200 font-medium not-italic">{fatherName}</span>
                                    {fatherPhone && (
                                        <span className="flex items-center gap-0.5 text-blue-400 not-italic">
                                            <HiPhone size={9}/>
                                            {fatherPhone}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        {motherName && (
                            <div className="flex items-start gap-1 italic">
                                <span className="text-color-400 whitespace-no-wrap flex-shrink-0">{t('mother')} :</span>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-color-200 font-medium not-italic">{motherName}</span>
                                    {motherPhone && (
                                        <span className="flex items-center gap-0.5 text-blue-400 not-italic">
                                            <HiPhone size={9}/>
                                            {motherPhone}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Context menu */}
            <div className="absolute right-5 top-5 contextIcon">
                {renderContextMenu(item)}
            </div>
        </div>
    )
}